'use client';

import Link from 'next/link';
import { useTranslation } from '@/core/i18n';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

export function Breadcrumb({ items }: BreadcrumbProps) {
  // Hook BEFORE the early return, not after: with an empty `items` the
  // component returned first and never called useTranslation, so React saw a
  // different hook count than on a render with breadcrumbs. It matches hooks by
  // position, so the counts must not vary between renders. Unlike the four
  // helpers fixed alongside this, `t` IS used here — so the hook is hoisted,
  // not removed.
  // `dir` is deliberately NOT destructured: RTL here is handled by the
  // `rtl:rotate-180` Tailwind variant on the chevron, which keys off the `dir`
  // attribute set on <html> in app/layout.tsx. Reading `dir` in JS and
  // branching would duplicate that, not complete it.
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <nav id="topbar-breadcrumb" aria-label={t('shared.breadcrumb.label')} className="flex items-center gap-1.5 min-w-0">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <div key={`${item.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
             {i > 0 && (
               <svg className="w-3.5 h-3.5 text-hms-textDim flex-shrink-0 rtl:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                 <polyline points="9 18 15 12 9 6" />
               </svg>
             )}
            {isLast || !item.href ? (
              <span className="text-[13px] font-semibold text-hms-text truncate">{item.label}</span>
            ) : (
              <Link href={item.href} className="text-[13px] font-medium text-hms-textDim hover:text-hms-textSub transition-colors truncate">
                {item.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}
